import {
  createRouter,
  createWebHistory,
  createMemoryHistory,
} from "vue-router";
import {
  isCapitalProvincia,
  getCapitalBySlug,
  getAllCapitalesSlugs,
} from "@/utils/provinceMapping";
import {
  routeDefinitions,
  SUPPORTED_LOCALES,
  DEFAULT_LOCALE,
} from "./route-data.js";
import { isProductionHost } from "@/utils/env";

// Mapa nombre → componente lazy import
const componentMap = {
  home: () => import("../pages/HomeDispatcher.vue"),
  test: () => import("../pages/SpeedTest/TestDemo"),
  mapa: () => import("../pages/ViewMap/MapV2"),
  datos: () => import("../pages/OpenData/OpenTests"),
  datosAbiertosDetail: () => import("../pages/TestDetail"),
  historial: () => import("../pages/History/History"),
  historialDetail: () => import("../pages/TestDetail"),
  estadisticas: () => import("../pages/Statistics/Statistics"),
  estadisticasUbicacion: () => import("../pages/Statistics/Statistics"),
  estadisticasUbicacionCP: () => import("../pages/Statistics/Statistics"),
  cnmcData: () => import("../pages/CNMCData/CNMCData"),
  servicios: () => import("../pages/MoreInfo/Services.vue"),
  faq: () => import("../pages/MoreInfo/FAQ.vue"),
  apps: () => import("../pages/MoreInfo/MobileApps.vue"),
  configuracion: () => import("../pages/MoreInfo/Settings.vue"),
  politicaPrivacidad: () => import("../pages/PrivacyPolicy/PrivacyPolicy.vue"),
  accesibilidad: () => import("../pages/MoreInfo/Accessibility.vue"),
  mapaSitio: () => import("../pages/MoreInfo/SiteMap.vue"),
  iframe: () => import("../pages/MoreInfo/IFrame.vue"),
  opiniones: () => import("../pages/MoreInfo/Opiniones.vue"),
  medios: () => import("../pages/MoreInfo/Prensa.vue"),
  derechos: () => import("../pages/MoreInfo/DerechosReclamaciones.vue"),
  politicaCookies: () => import("@/pages/MoreInfo/CookiePolicy.vue"),
  testEmbed: () => import("../pages/SpeedTest/TestDemoEmbed.vue"),
};

// Mapa para encontrar rutas equivalentes entre idiomas
const routePathMap = {};

// Rutas ocultas (sin multiidioma, no indexables)
const hiddenRoutes = [
  {
    path: "/_preview/home-v2",
    name: "homeV2Preview",
    component: () => import("../pages/HomeV2.vue"),
    meta: { isHidden: true, excludeFromSitemap: true, breadcrumb: "Home V2 Preview" },
  },
];

// Redirecciones de URLs antiguas (legacy) a nuevas
const legacyRedirects = [
  {
    path: "/Test",
    redirect: (to) => ({ path: "/test-velocidad", query: to.query }),
  },
  { path: "/Mapa", redirect: "/mapa-cobertura" },
  { path: "/TestAbiertos", redirect: "/datos-abiertos" },
  {
    path: "/TestAbiertos/:idHistorial",
    redirect: (to) => `/datos-abiertos/${to.params.idHistorial}`,
  },
  { path: "/Historial", redirect: "/historial" },
  {
    path: "/Historial/:idHistorial",
    redirect: (to) => `/historial/${to.params.idHistorial}`,
  },
  { path: "/Estadisticas", redirect: "/estadisticas" },
  {
    path: "/Estadisticas/:ubicacion",
    redirect: (to) => `/estadisticas/${to.params.ubicacion}`,
  },
  { path: "/CNMCData", redirect: "/cnmc-data" },
  { path: "/MasInformacion/Servicios", redirect: "/servicios" },
  {
    path: "/MasInformacion/PreguntasFrecuentes",
    redirect: "/preguntas-frecuentes",
  },
  {
    path: "/MasInformacion/AplicacionesMoviles",
    redirect: "/aplicaciones-moviles",
  },
  { path: "/MasInformacion/Configuracion", redirect: "/configuracion" },
  { path: "/informacionTCalidad", redirect: "/informacion-legal" },
  { path: "/Accesibilidad", redirect: "/accesibilidad" },
  { path: "/MapaSitio", redirect: "/mapa-sitio" },
  { path: "/iframe", redirect: "/widget" },
  { path: "/faq", redirect: "/preguntas-frecuentes" },
  { path: "/apps", redirect: "/aplicaciones-moviles" },
  { path: "/politica-privacidad", redirect: "/informacion-legal" },
  { path: "/datos", redirect: "/datos-abiertos" },
  // Renombrado de "Sala de Prensa" a "Actualidad" a petición del equipo web CNMC,
  // para evitar confusión con el apartado homónimo de la web genérica.
  { path: "/prensa", redirect: "/medios" },
  { path: "/en/press", redirect: "/en/media" },
  // Redirects de /ciudad a /estadisticas/:slug
  {
    path: "/ciudad/:slug",
    redirect: (to) => `/estadisticas/${to.params.slug}`,
  },
  {
    path: "/ciudad/:slug/:codigoPostal",
    redirect: (to) => `/estadisticas/${to.params.slug}/${to.params.codigoPostal}`,
  },
  // Redirects de URLs antiguas con /ciudad/
  {
    path: "/estadisticas/ciudad/:slug",
    redirect: (to) => `/estadisticas/${to.params.slug}`,
  },
  {
    path: "/estadisticas/ciudad/:slug/:codigoPostal",
    redirect: (to) => `/estadisticas/${to.params.slug}/${to.params.codigoPostal}`,
  },
  // Inglés
  {
    path: "/en/Test",
    redirect: (to) => ({ path: "/en/speed-test", query: to.query }),
  },
  { path: "/en/Mapa", redirect: "/en/coverage-map" },
  { path: "/en/TestAbiertos", redirect: "/en/open-data" },
  {
    path: "/en/TestAbiertos/:idHistorial",
    redirect: (to) => `/en/open-data/${to.params.idHistorial}`,
  },
  { path: "/en/Historial", redirect: "/en/history" },
  {
    path: "/en/Historial/:idHistorial",
    redirect: (to) => `/en/history/${to.params.idHistorial}`,
  },
  { path: "/en/Estadisticas", redirect: "/en/statistics" },
  {
    path: "/en/Estadisticas/:ubicacion",
    redirect: (to) => `/en/statistics/${to.params.ubicacion}`,
  },
  { path: "/en/CNMCData", redirect: "/en/cnmc-data" },
  { path: "/en/MasInformacion/Servicios", redirect: "/en/services" },
  { path: "/en/MasInformacion/PreguntasFrecuentes", redirect: "/en/faq" },
  {
    path: "/en/MasInformacion/AplicacionesMoviles",
    redirect: "/en/mobile-apps",
  },
  { path: "/en/MasInformacion/Configuracion", redirect: "/en/settings" },
  { path: "/en/informacionTCalidad", redirect: "/en/legal-information" },
  { path: "/en/Accesibilidad", redirect: "/en/accessibility" },
  { path: "/en/MapaSitio", redirect: "/en/sitemap" },
  { path: "/en/iframe", redirect: "/en/widget" },
  { path: "/en/apps", redirect: "/en/mobile-apps" },
  { path: "/en/politica-privacidad", redirect: "/en/legal-information" },
  { path: "/en/datos", redirect: "/en/open-data" },
  // Redirects de /en/city a /en/statistics/city
  {
    path: "/en/city/:slug",
    redirect: (to) => `/en/statistics/city/${to.params.slug}`,
  },
  {
    path: "/en/city/:slug/:codigoPostal",
    redirect: (to) => `/en/statistics/city/${to.params.slug}/${to.params.codigoPostal}`,
  },
  // URLs españolas con prefijo /en
  { path: "/en/test-velocidad", redirect: "/en/speed-test" },
  { path: "/en/mapa-cobertura", redirect: "/en/coverage-map" },
  { path: "/en/datos-abiertos", redirect: "/en/open-data" },
  {
    path: "/en/datos-abiertos/:idHistorial",
    redirect: (to) => `/en/open-data/${to.params.idHistorial}`,
  },
  { path: "/en/historial", redirect: "/en/history" },
  {
    path: "/en/historial/:idHistorial",
    redirect: (to) => `/en/history/${to.params.idHistorial}`,
  },
  { path: "/en/estadisticas", redirect: "/en/statistics" },
  {
    path: "/en/estadisticas/:ubicacion",
    redirect: (to) => `/en/statistics/${to.params.ubicacion}`,
  },
  { path: "/en/servicios", redirect: "/en/services" },
  { path: "/en/preguntas-frecuentes", redirect: "/en/faq" },
  { path: "/en/aplicaciones-moviles", redirect: "/en/mobile-apps" },
  { path: "/en/configuracion", redirect: "/en/settings" },
  { path: "/en/informacion-legal", redirect: "/en/legal-information" },
  { path: "/en/accesibilidad", redirect: "/en/accessibility" },
  { path: "/en/mapa-sitio", redirect: "/en/sitemap" },
  { path: "/en/politica-cookies", redirect: "/en/cookie-policy" },
];

// Función para generar rutas localizadas
function generateLocalizedRoutes() {
  const routes = [];

  routeDefinitions.forEach((def) => {
    if (!def.paths) return;

    const component = componentMap[def.name];
    if (!component) return;

    routePathMap[def.name] = {};

    // Ruta en español (sin prefijo)
    const esPath = def.paths.es === "" ? "/" : `/${def.paths.es}`;
    routePathMap[def.name].es = esPath;

    routes.push({
      path: esPath,
      name: def.name,
      component,
      meta: {
        ...def.meta,
        locale: "es",
        routeName: def.name,
        alternatePaths: {
          es: esPath,
          en: `/en/${def.paths.en}`.replace(/\/$/, "") || "/en",
        },
      },
    });

    // Ruta en inglés (con prefijo /en)
    const enPath = def.paths.en === "" ? "/en" : `/en/${def.paths.en}`;
    routePathMap[def.name].en = enPath;

    routes.push({
      path: enPath,
      name: `${def.name}-en`,
      component,
      meta: {
        ...def.meta,
        locale: "en",
        routeName: def.name,
        alternatePaths: { es: esPath, en: enPath },
      },
    });
  });

  routes.push(...legacyRedirects);
  routes.push(...hiddenRoutes);

  // Ruta 404 (debe ir al final)
  routes.push({
    path: "/:pathMatch(.*)*",
    name: "notFound",
    component: () => import("../pages/NotFound.vue"),
    meta: { titleKey: "seo.titles.notFound", isErrorPage: true },
  });

  return routes;
}

const routes = generateLocalizedRoutes();

// Configuración del scroll behavior
const scrollBehavior = (to, from, savedPosition) => {
  if (to.hash) {
    return { el: to.hash, behavior: "smooth", top: 130 };
  } else if (savedPosition) {
    return savedPosition;
  } else {
    return { top: 0 };
  }
};

// Usar createMemoryHistory en SSR (Node), createWebHistory en cliente
const isSSR = typeof window === "undefined";

const router = createRouter({
  history: isSSR ? createMemoryHistory() : createWebHistory(),
  routes,
  scrollBehavior,
});

// Rutas ocultas (/_admin/*, /_preview/*) solo accesibles fuera del hostname de
// producción. Así Claudi y el equipo pueden verlas en pre y en local, pero a
// un usuario final en calidadtelecos.cnmc.gob.es le redirige a la home si
// intenta navegar a una de ellas aunque conozca la URL.
router.beforeEach((to, _from, next) => {
  if (to.meta?.isHidden && isProductionHost()) {
    next({ path: "/", replace: true });
    return;
  }
  next();
});

// Tras un deploy, los hashes de los chunks lazy cambian. Una pestaña abierta
// con el HTML anterior intenta cargar un chunk que ya no existe → import()
// falla → Vue no renderiza nada y la página queda en blanco. Al detectar ese
// error recargamos para que el navegador pida el HTML nuevo con los hashes
// actuales. Solo actuamos sobre errores de carga de módulo; otros los dejamos
// propagar para no ocultar bugs reales.
if (typeof window !== "undefined") {
  const CHUNK_ERROR_RE = /Failed to fetch dynamically imported module|Importing a module script failed|Loading chunk \d+ failed|ChunkLoadError/i;
  const reloadOnChunkError = (err) => {
    const msg = err?.message || String(err || "");
    if (CHUNK_ERROR_RE.test(msg) && !sessionStorage.getItem("chunk-reload-attempted")) {
      sessionStorage.setItem("chunk-reload-attempted", "1");
      window.location.reload();
    }
  };
  router.onError(reloadOnChunkError);
  window.addEventListener("vite:preloadError", reloadOnChunkError);
  // Limpiar el flag cuando una navegación se completa con éxito.
  router.afterEach(() => {
    sessionStorage.removeItem("chunk-reload-attempted");
  });
}

// ============================================================================
// HELPERS PÚBLICOS
// ============================================================================

export function getLocaleFromRoute(route) {
  if (route.meta?.locale) return route.meta.locale;
  if (route.path.startsWith("/en")) return "en";
  return DEFAULT_LOCALE;
}

export function getBaseRouteName(routeName) {
  if (!routeName) return null;
  return routeName.replace(/-en$/, "");
}

export function getAlternateUrl(route, targetLocale, includeQuery = false) {
  const baseUrl = "https://calidadtelecos.cnmc.gob.es";
  const currentLocale = getLocaleFromRoute(route);

  if (currentLocale === targetLocale) {
    const queryString =
      includeQuery && route.query && Object.keys(route.query).length > 0
        ? `?${new URLSearchParams(route.query).toString()}`
        : "";
    return `${baseUrl}${route.path}${queryString}`;
  }

  if (route.meta?.alternatePaths) {
    let alternatePath = route.meta.alternatePaths[targetLocale];

    if (route.params) {
      Object.keys(route.params).forEach((param) => {
        alternatePath = alternatePath.replace(`:${param}`, route.params[param]);
      });
    }

    const queryString =
      includeQuery && route.query && Object.keys(route.query).length > 0
        ? `?${new URLSearchParams(route.query).toString()}`
        : "";

    const hash = route.hash || "";

    return `${baseUrl}${alternatePath}${queryString}${hash}`;
  }

  const baseName = getBaseRouteName(route.name);
  if (baseName && routePathMap[baseName]) {
    let path = routePathMap[baseName][targetLocale];

    if (route.params) {
      Object.keys(route.params).forEach((param) => {
        path = path.replace(`:${param}`, route.params[param]);
      });
    }

    return `${baseUrl}${path}`;
  }

  return `${baseUrl}${targetLocale === "en" ? "/en" : "/"}`;
}

export function navigateToLocale(router, route, targetLocale) {
  const currentLocale = getLocaleFromRoute(route);
  if (currentLocale === targetLocale) return;

  const baseName = getBaseRouteName(route.name);

  if (baseName && routePathMap[baseName]) {
    let targetPath = routePathMap[baseName][targetLocale];

    if (route.params) {
      Object.keys(route.params).forEach((param) => {
        targetPath = targetPath.replace(`:${param}`, route.params[param]);
      });
    }

    router.push({
      path: targetPath,
      query: route.query,
      hash: route.hash,
    });
  }
}

export function getSitemapRoutes() {
  const baseRoutes = routeDefinitions
    .filter((def) => !def.meta?.excludeFromSitemap && !def.meta?.isHidden)
    .map((def) => ({
      name: def.name,
      es: def.paths.es === "" ? "/" : `/${def.paths.es}`,
      en: def.paths.en === "" ? "/en" : `/en/${def.paths.en}`,
    }));

  // Añadir rutas de capitales de provincia para estadísticas
  const capitalesSlugs = getAllCapitalesSlugs();
  const capitalesRoutes = capitalesSlugs.map((slug) => ({
    name: `estadisticas-${slug}`,
    es: `/estadisticas/${slug}`,
    en: `/en/statistics/${slug}`,
  }));

  return [...baseRoutes, ...capitalesRoutes];
}

// ============================================================================
// EXPORTS
// ============================================================================

export { SUPPORTED_LOCALES, DEFAULT_LOCALE, routePathMap, routes, scrollBehavior };
export default router;
